function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import log, { warn, isDebug, reportError } from './utility/debug.js';
import { scheduleOnNextTick, unscheduleOnNextTick } from './utility/scheduleOnNextTick.js';
import { LAYOUT_REASON } from './Layout.js';
import ItemNotRenderedError from './ItemNotRenderedError.js';
export default function () {
  var _this = this;

  this.onUpdateShownItemIndexes = function (_ref) {
    var reason = _ref.reason,
        stateUpdate = _ref.stateUpdate;

    // In case of "don't perform this particular re-layout
    // because a more-priority one is already scheduled".
    var skip = function skip() {
      if (stateUpdate) {
        // Don't update layout. Just update some state.
        // `stateUpdate` isn't supposed to affect the layout in any way
        // because it doesn't contain any properties that affect the rendering of the list.
        // I.e. it doesn't contain properties like `beforeItemsHeight` or `afterItemsHeight`, etc.
        _this.updateState(stateUpdate);
      }
    }; // If new `items` have been set and are waiting to be applied,
    // or if the viewport width has changed requiring a re-layout,
    // then temporarily stop all other updates like "on scroll" updates.
    //
    // This prevents `state` from becoming inconsistent, because several
    // "contenders" (layout reasons) could be attempting to update
    // `VirtualScroller` state in parallel between two successive renders.
    //
    // For example, both `VirtualScroller.setItems()` and `VirtualScroller.scroll.onScroll()`
    // "contenders" could be attempting to update `VirtualScroller` state in parallel,
    // and the layout calculated by one of them would overwrite the layout calculated by another.
    //
    // Which one should have the priority in such case then?
    // In this specific example, `VirtualScroller.setItems()` should be the one
    // because only it knows the correct new `items` whereas other "contenders"
    // have the old stale `items`.
    //
    // Another example of a "contender" that should have a priority is
    // "on list width has changed" handler because only it has access
    // to the correct new width of the list rather than the old stale width.
    //
    // So in these two "priority" cases any other re-layout should be ignored.
    //


    if (_this.newItemsWillBeRendered || _this.widthHasChanged || _this._isResizing) {
      return skip();
    } // If there're no items then there's no need to re-layout anything.


    if (_this.getItemsCount() === 0) {
      return skip();
    } // Cancel a scheduled "re-layout when user stops scrolling" timer
    // because a re-layout will be performed here now.


    _this.scroll.cancelScheduledLayout(); // Cancel a scheduled re-layout that is scheduled to run at the next "frame",
    // because a re-layout will be performed here now.


    stateUpdate = _this.cancelLayoutTimer({
      stateUpdate: stateUpdate
    }); // Perform a re-layout.

    log("~ Update Layout (on ".concat(reason, ") ~"));
    updateShownItemIndexes.call(_this, {
      stateUpdate: stateUpdate
    });
  };
  /**
   * Updates the "from" and "to" shown item indexes.
   * If the list is visible and some of the items being shown are new
   * and are required to be measured first, then
   * `firstNonMeasuredItemIndex` is defined.
   * If the list is visible and all items being shown have been encountered
   * (and measured) before, then `firstNonMeasuredItemIndex` is `undefined`.
   *
   * The `stateUpdate` parameter is just an optional "additional" state update.
   */


  function updateShownItemIndexes(_ref2) {
    var stateUpdate = _ref2.stateUpdate;
    var startedAt = Date.now(); // Get shown item indexes.

    var _getShownItemIndexes$ = getShownItemIndexes.call(this),
        firstShownItemIndex = _getShownItemIndexes$.firstShownItemIndex,
        lastShownItemIndex = _getShownItemIndexes$.lastShownItemIndex,
        shownItemsHeight = _getShownItemIndexes$.shownItemsHeight,
        firstNonMeasuredItemIndex = _getShownItemIndexes$.firstNonMeasuredItemIndex; // If scroll position is scheduled to be restored after render,
    // then the "anchor" item must be rendered, and all of the prepended
    // items before it, all in a single pass. This way, all of the
    // prepended items' heights could be measured right after the render
    // has finished, and the scroll position can then be immediately restored.


    if (this.listHeightMeasurement.hasSnapshot()) {
      if (lastShownItemIndex < this.listHeightMeasurement.getAnchorItemIndex()) {
        lastShownItemIndex = this.listHeightMeasurement.getAnchorItemIndex();
      } // `firstShownItemIndex` is always `0` when prepending items.
      // And `lastShownItemIndex` always covers all prepended items in this case.
      // None of the prepended items have been rendered before,
      // so their heights are unknown. The code at the start of this function
      // did therefore set `firstNonMeasuredItemIndex` to non-`undefined`
      // in order to render just the first prepended item in order to
      // measure it, and only then make a decision on how many other
      // prepended items to render. But since we've instructed the code
      // to show all of the prepended items at once, there's no need to
      // "redo layout after render". Additionally, if layout was re-done
      // after render, then there would be a short interval of visual
      // "jitter" due to the scroll position not being restored because it'd
      // wait for the second layout to finish instead of being restored
      // right after the first one.


      firstNonMeasuredItemIndex = undefined;
    } // Validate the heights of items to be hidden on next render.
    // For example, a user could click a "Show more" button,
    // or an "Expand YouTube video" button, which would result
    // in the actual height of the list item being different
    // from what has been initially measured in `this.itemHeights[i]`,
    // if the developer didn't call `.setItemState(item, newState)` and `.onItemHeightDidChange(item)`.


    if (!validateWillBeHiddenItemHeightsAreAccurate.call(this, firstShownItemIndex, lastShownItemIndex)) {
      log('~ Because some of the will-be-hidden item heights (listed above) have changed since they\'ve last been measured, redo layout. ~'); // Redo layout, now with the correct item heights.

      return updateShownItemIndexes.call(this, {
        stateUpdate: stateUpdate
      });
    } // Measure "before" items height.


    var beforeItemsHeight = this.layout.getBeforeItemsHeight(firstShownItemIndex); // Measure "after" items height.

    var afterItemsHeight = this.layout.getAfterItemsHeight(lastShownItemIndex, this.getItemsCount());
    var layoutDuration = Date.now() - startedAt; // Debugging.

    log('~ Calculated Layout' + (this.isInBypassMode() ? ' (bypass)' : '') + ' ~');

    if (layoutDuration < SLOW_LAYOUT_DURATION) {// log('Calculated in', layoutDuration, 'ms')
    } else {
      // Getting this warning would mean that for a given (large) count of items,
      // calculating `beforeItemsHeight` or `afterItemsHeight` requires too much time
      // in order to iterate over all not-currently-visible items to add up their heights.
      //
      // Because layout calculation is a blocking procedure, it "freezes" the application
      // for a given amount of milliseconds. If the amount of those milliseconds is longer
      // than 16ms, frames are being dropped in a 60fps scenario.
      // Modern phones even use 120fps as a baseline, so the timeframe requirement
      // gets even more strict.
      //
      // What could be a way of dealing with this?
      //
      // * The code could cache clustered item heights in order to perform less add-up operations.
      //   For example, the code could cache the total height of items 1..100, 101..200, etc.
      //   Then 1..1000, 1001..2000, etc. Then 1..10000, 10001..20000, etc. So the clusters
      //   would just get progressively larger as more `items` are added.
      //
      warn('Layout calculation time too high:', layoutDuration, 'ms');
    }

    if (this.getColumnsCount()) {
      log('Columns count', this.getColumnsCount());
    }

    log('First shown item index', firstShownItemIndex);
    log('Last shown item index', lastShownItemIndex);
    log('Before items height', beforeItemsHeight);
    log('After items height (actual or estimated)', afterItemsHeight);
    log('Average item height (will be used to calculate estimated "after items height")', this.getAverageItemHeight());

    if (isDebug()) {
      log('Item heights', this.getState().itemHeights.slice());
      log('Item states', this.getState().itemStates.slice());
    } // Optionally preload items to be rendered.


    this.onBeforeShowItems(this.getState().items, this.getState().itemHeights, firstShownItemIndex, lastShownItemIndex); // Set `this.firstNonMeasuredItemIndex`.

    this.firstNonMeasuredItemIndex = firstNonMeasuredItemIndex; // if (firstNonMeasuredItemIndex !== undefined) {
    // 	log('Non-measured item index that will be measured at next layout', firstNonMeasuredItemIndex)
    // }
    // Set "previously calculated layout".
    //
    // The "previously calculated layout" feature is not currently used.
    //
    // The current layout snapshot could be stored as a "previously calculated layout" variable
    // so that it could theoretically be used when calculating new layout incrementally
    // rather than from scratch, which would be an optimization.
    //
    // Currently, this feature is not used, and `shownItemsHeight` property
    // is not returned at all, so don't set any "previously calculated layout".
    //

    if (shownItemsHeight === undefined) {
      this.previouslyCalculatedLayout = undefined;
    } else {
      // If "previously calculated layout" feature would be implmeneted,
      // then this code would set "previously calculate layout" instance variable.
      //
      // What for would this instance variable be used?
      //
      // Instead of using a `this.previouslyCalculatedLayout` instance variable,
      // this code could use `this.getState()` because it reflects what's currently on screen,
      // but there's a single edge case when it could go out of sync —
      // updating item heights externally via `.onItemHeightDidChange(item)`.
      //
      // If, for example, an item height was updated externally via `.onItemHeightDidChange(item)`
      // then `this.getState().itemHeights` would get updated immediately but
      // `this.getState().beforeItemsHeight` or `this.getState().afterItemsHeight`
      // would still correspond to the previous item height, so those would be "stale".
      // On the other hand, same values in `this.previouslyCalculatedLayout` instance variable
      // can also be updated immediately, so they won't go out of sync with the updated item height.
      // That seems the only edge case when using a separate `this.previouslyCalculatedLayout`
      // instance variable instead of using `this.getState()` would theoretically be justified.
      //
      this.previouslyCalculatedLayout = {
        firstShownItemIndex: firstShownItemIndex,
        lastShownItemIndex: lastShownItemIndex,
        beforeItemsHeight: beforeItemsHeight,
        shownItemsHeight: shownItemsHeight
      };
    } // Update `VirtualScroller` state.
    // `VirtualScroller` automatically re-renders on state updates.
    //
    // All `state` properties updated here should be overwritten in
    // the implementation of `setItems()` and `onResize()` methods
    // so that the `state` is not left in an inconsistent state
    // whenever there're concurrent `updateState()` updates that could
    // possibly conflict with one another — instead, those state updates
    // should overwrite each other in terms of priority.
    // These "on scroll" updates have the lowest priority compared to
    // the state updates originating from `setItems()` and `onResize()` methods.
    //


    this.updateState(_objectSpread({
      firstShownItemIndex: firstShownItemIndex,
      lastShownItemIndex: lastShownItemIndex,
      beforeItemsHeight: beforeItemsHeight,
      afterItemsHeight: afterItemsHeight
    }, stateUpdate));
  }

  function getCoordinatesOfVisibleAreaInsideTheList() {
    var visibleAreaBounds = this.scroll.getVisibleAreaBounds();
    this.latestLayoutVisibleArea = visibleAreaBounds; // Subtract the top offset of the list inside the scrollable container.

    var listTopOffsetInsideScrollableContainer = this.getListTopOffsetInsideScrollableContainer();
    return {
      top: visibleAreaBounds.top - listTopOffsetInsideScrollableContainer,
      bottom: visibleAreaBounds.bottom - listTopOffsetInsideScrollableContainer
    };
  }

  function getShownItemIndexes() {
    var itemsCount = this.getItemsCount();
    var visibleAreaInsideTheList = getCoordinatesOfVisibleAreaInsideTheList.call(this);

    if (this.isInBypassMode()) {
      return {
        firstShownItemIndex: 0,
        lastShownItemIndex: itemsCount - 1 // shownItemsHeight: this.getState().itemHeights.reduce((sum, itemHeight) => sum + itemHeight, 0)

      };
    } // Find the indexes of the items that are currently visible
    // (or close to being visible) in the scrollable container.
    // For scrollable containers other than the main screen, it could also
    // check the visibility of such scrollable container itself, because it
    // might be not visible.
    // If such kind of an optimization would hypothetically be implemented,
    // then it would also require listening for "scroll" events on the screen.
    // Overall, I suppose that such "actual visibility" feature would be
    // a very minor optimization and not something I'd deal with.


    var isVisible = visibleAreaInsideTheList.top < this.itemsContainer.getHeight() + this.layout.getPrerenderMargin() && visibleAreaInsideTheList.bottom > 0 - this.layout.getPrerenderMargin();

    if (!isVisible) {
      log('The entire list is off-screen. No items are visible.');
      return this.layout.getNonVisibleListShownItemIndexes();
    } // Get shown item indexes.


    return this.layout.getShownItemIndexes({
      itemsCount: this.getItemsCount(),
      visibleAreaInsideTheList: visibleAreaInsideTheList
    });
  }
  /**
   * Validates the heights of items to be hidden on next render.
   * For example, a user could click a "Show more" button,
   * or an "Expand YouTube video" button, which would result
   * in the actual height of the list item being different
   * from what has been initially measured in `this.itemHeights[i]`,
   * if the developer didn't call `.setItemState(item, newState)` and `.onItemHeightDidChange(item)`.
   */


  function validateWillBeHiddenItemHeightsAreAccurate(firstShownItemIndex, lastShownItemIndex) {
    var isValid = true;
    var i = this.getState().firstShownItemIndex;

    while (i <= this.getState().lastShownItemIndex) {
      if (i >= firstShownItemIndex && i <= lastShownItemIndex) {// The item's still visible.
      } else {
        // The item will be hidden. Re-measure its height.
        // The rationale is that there could be a situation when an item's
        // height has changed, and the developer has properly added an
        // `.onItemHeightDidChange(item)` call to notify `VirtualScroller`
        // about that change, but at the same time that wouldn't work.
        // For example, suppose there's a list of several items on a page,
        // and those items are in "minimized" state (having height 100px).
        // Then, a user clicks an "Expand all items" button, and all items
        // in the list are expanded (expanded item height is gonna be 700px).
        // `VirtualScroller` demands that `.onItemHeightDidChange(item)` is called
        // in such cases, and the developer has properly added the code to do that.
        // So, if there were 10 "minimized" items visible on a page, then there
        // will be 10 individual `.onItemHeightDidChange(item)` calls. No issues so far.
        // But, as the first `.onItemHeightDidChange(item)` call executes, it immediately
        // ("synchronously") triggers a re-layout, and that re-layout finds out
        // that now, because the first item is big, it occupies most of the screen
        // space, and only the first 3 items are visible on screen instead of 10,
        // and so it leaves the first 3 items mounted and unmounts the rest 7.
        // Then, after `VirtualScroller` has rerendered, the code returns to
        // where it was executing, and calls `.onItemHeightDidChange(item)` for the
        // second item. It also triggers an immediate re-layout that finds out
        // that only the first 2 items are visible on screen, and it unmounts
        // the third one too. After that, it calls `.onItemHeightDidChange(item)`
        // for the third item, but that item is no longer rendered, so its height
        // can't be measured, and the same's for all the rest of the original 10 items.
        // So, even though the developer has written their code properly, the
        // `VirtualScroller` still ends up having incorrect `itemHeights[]`:
        // `[700px, 700px, 100px, 100px, 100px, 100px, 100px, 100px, 100px, 100px]`
        // while it should have been `700px` for all of them.
        // To work around such issues, every item's height is re-measured before it
        // gets hidden.
        var previouslyMeasuredItemHeight = this.getState().itemHeights[i];
        var actualItemHeight = remeasureItemHeight.call(this, i);

        if (actualItemHeight !== previouslyMeasuredItemHeight) {
          if (isValid) {
            log('~ Validate will-be-hidden item heights. ~'); // Update or reset previously calculated layout.

            updatePreviouslyCalculatedLayoutOnItemHeightChange.call(this, i, previouslyMeasuredItemHeight, actualItemHeight);
          }

          isValid = false;
          warn('Item index', i, 'is no longer visible and will be unmounted. Its height has changed from', previouslyMeasuredItemHeight, 'to', actualItemHeight, 'since it was last measured. This is not necessarily a bug, and could happen, for example, on screen width change, or when there\'re several `onItemHeightDidChange(item)` calls issued at the same time, and the first one triggers a re-layout before the rest of them have had a chance to be executed.');
        }
      }

      i++;
    }

    return isValid;
  }

  function remeasureItemHeight(i) {
    var _this$getState = this.getState(),
        firstShownItemIndex = _this$getState.firstShownItemIndex;

    return this.itemHeights.remeasureItemHeight(i, firstShownItemIndex);
  } // Updates the snapshot of the current layout when an item's height changes.
  //
  // The "previously calculated layout" feature is not currently used.
  //
  // The current layout snapshot could be stored as a "previously calculated layout" variable
  // so that it could theoretically be used when calculating new layout incrementally
  // rather than from scratch, which would be an optimization.
  //


  function updatePreviouslyCalculatedLayoutOnItemHeightChange(i, previousHeight, newHeight) {
    var prevLayout = this.previouslyCalculatedLayout;

    if (prevLayout) {
      var heightDifference = newHeight - previousHeight;

      if (i < prevLayout.firstShownItemIndex) {
        // Patch `prevLayout`'s `.beforeItemsHeight`.
        prevLayout.beforeItemsHeight += heightDifference;
      } else if (i > prevLayout.lastShownItemIndex) {
        // Could patch `.afterItemsHeight` of `prevLayout` here,
        // if `.afterItemsHeight` property existed in `prevLayout`.
        if (prevLayout.afterItemsHeight !== undefined) {
          prevLayout.afterItemsHeight += heightDifference;
        }
      } else {
        // Patch `prevLayout`'s shown items height.
        prevLayout.shownItemsHeight += newHeight - previousHeight;
      }
    }
  }
  /**
   * Returns the list's top offset relative to the scrollable container's top edge.
   * @return {number}
   */


  this.getListTopOffsetInsideScrollableContainer = function () {
    var listTopOffset = _this.scrollableContainer.getItemsContainerTopOffset();

    if (_this.listTopOffsetWatcher) {
      _this.listTopOffsetWatcher.onListTopOffset(listTopOffset);
    }

    return listTopOffset;
  };

  this._onItemHeightDidChange = function (itemOrIndex) {
    // Item index.
    var i = _this._getItemIndexByItemOrIndex(itemOrIndex); // If the item wasn't found, the error was already reported,
    // so just don't do anything else.


    if (i === undefined) {
      return;
    }

    var _this$getState2 = _this.getState(),
        itemHeights = _this$getState2.itemHeights,
        firstShownItemIndex = _this$getState2.firstShownItemIndex,
        lastShownItemIndex = _this$getState2.lastShownItemIndex;

    log('~ On Item Height Did Change was called ~');
    log('Item index', i); // Check if the item is still rendered.

    if (!(i >= firstShownItemIndex && i <= lastShownItemIndex)) {
      // There could be valid cases when an item is no longer rendered
      // by the time `.onItemHeightDidChange(item)` gets called.
      // For example, suppose there's a list of several items on a page,
      // and those items are in "minimized" state (having height 100px).
      // Then, a user clicks an "Expand all items" button, and all items
      // in the list are expanded (expanded item height is gonna be 700px).
      // `VirtualScroller` demands that `.onItemHeightDidChange(item)` is called
      // in such cases, and the developer has properly added the code to do that.
      // So, if there were 10 "minimized" items visible on a page, then there
      // will be 10 individual `.onItemHeightDidChange(item)` calls. No issues so far.
      // But, as the first `.onItemHeightDidChange(item)` call executes, it immediately
      // ("synchronously") triggers a re-layout, and that re-layout finds out
      // that now, because the first item is big, it occupies most of the screen
      // space, and only the first 3 items are visible on screen instead of 10,
      // and so it leaves the first 3 items mounted and unmounts the rest 7.
      // Then, after `VirtualScroller` has rerendered, the code returns to
      // where it was executing, and calls `.onItemHeightDidChange(item)` for the
      // second item. It also triggers an immediate re-layout that finds out
      // that only the first 2 items are visible on screen, and it unmounts
      // the third one too. After that, it calls `.onItemHeightDidChange(item)`
      // for the third item, but that item is no longer rendered, so its height
      // can't be measured, and the same's for all the rest of the original 10 items.
      // So, even though the developer has written their code properly, there're
      // still situations when the item could be no longer rendered by the time
      // `.onItemHeightDidChange(item)` gets called.
      return warn('The item is no longer rendered. This is not necessarily a bug, and could happen, for example, when when a developer calls `onItemHeightDidChange(item)` while looping through a batch of items.');
    }

    var previousHeight = itemHeights[i];

    if (previousHeight === undefined) {
      // There're valid cases when the item still hasn't been measured and `onItemHeightDidChange(item)`
      // function was called for it. That's because measuring items is only done after the `VirtualScroller`
      // has `start()`ed. But it's not necessarily `start()`ed by the time it has been rendered (mounted).
      // For example, the React component `<VirtualScroller/>` provides an `readyToStart={false}` property
      // in order to let the application finish initializing itself before starting the `VirtualScroller`.
      // So there're legitimate cases when a `VirtualScroller` is already rendered but is not `start()`ed yet.
      // In those cases, not re-measuring the item's height when it changes would not result in any bug
      // because the item height will be re-measured anyway the first time it's rendered.
      // So in such situations, re-measuring the item's height can be skipped without any consequence.
      //
      // return reportError(`"onItemHeightDidChange()" has been called for item index ${i} but the item hasn't been rendered before.`)
      return;
    }

    log('~ Re-measure item height ~');
    var newHeight;

    try {
      newHeight = remeasureItemHeight.call(_this, i);
    } catch (error) {
      // Successfully finishing an `onItemHeightDidChange(item)` call is not considered
      // critical for `VirtualScroller`'s operation, so such errors could be ignored.
      if (error instanceof ItemNotRenderedError) {
        return reportError("\"onItemHeightDidChange()\" has been called for item index ".concat(i, " but the item is not currently rendered and can't be measured. The exact error was: ").concat(error.message));
      }
    }

    log('Previous height', previousHeight);
    log('New height', newHeight);

    if (previousHeight !== newHeight) {
      log('~ Item height has changed. Should update layout. ~'); // Update or reset a previously calculated layout with the new item height
      // so that the potential future "diff"s based on that "previously calculated" layout
      // would be correct.
      //
      // The "previously calculated layout" feature is not currently used
      // so this function call doesn't really affect anything.
      //

      updatePreviouslyCalculatedLayoutOnItemHeightChange.call(_this, i, previousHeight, newHeight); // Recalculate layout.
      //
      // If the `VirtualScroller` is already waiting for a state update to be rendered,
      // delay `onItemHeightDidChange(item)`'s re-layout until that state update is rendered.
      // The reason is that React `<VirtualScroller/>`'s `onHeightDidChange()` is meant to
      // be called inside `useLayoutEffect()` hook. Due to how React is implemented internally,
      // that might happen in the middle of the currently pending `setState()` operation
      // being applied, resulting in weird "race condition" bugs.
      //

      if (_this._isActive) {
        if (_this.waitingForRender) {
          log('~ Another state update is already waiting to be rendered. Delay the layout update until then. ~');
          _this.updateLayoutAfterRenderBecauseItemHeightChanged = true;
        } else {
          _this.onUpdateShownItemIndexes({
            reason: LAYOUT_REASON.ITEM_HEIGHT_CHANGED
          });
        }
      } // If there was a request for `setState()` with new `items`, then the changes
      // to `currentState.itemHeights[]` made above in a `remeasureItemHeight()` call
      // would be overwritten when that pending `setState()` call gets applied.
      // To fix that, the updates to current `itemHeights[]` are noted in
      // `this.itemHeightsThatChangedWhileNewItemsWereBeingRendered` variable.
      // That variable is then checked when the `setState()` call with the new `items`
      // has been updated.


      if (_this.newItemsWillBeRendered) {
        if (!_this.itemHeightsThatChangedWhileNewItemsWereBeingRendered) {
          _this.itemHeightsThatChangedWhileNewItemsWereBeingRendered = {};
        }

        _this.itemHeightsThatChangedWhileNewItemsWereBeingRendered[String(i)] = newHeight;
      }
    }
  };

  this.getPrerenderMargin = function () {
    // The list component renders not only the items that're currently visible
    // but also the items that lie within some extra vertical margin (called
    // "prerender margin") on top and bottom for future scrolling: this way,
    // there'll be significantly less layout recalculations as the user scrolls,
    // because now it doesn't have to recalculate layout on each scroll event.
    // By default, the "prerender margin" is equal to the screen height:
    // this seems to be the optimal value for "Page Up" / "Page Down" navigation
    // and optimized mouse wheel scrolling (a user is unlikely to continuously
    // scroll past the screen height, because they'd stop to read through
    // the newly visible items first, and when they do stop scrolling, that's
    // when layout gets recalculated).
    return _this.scrollableContainer.getHeight() * _this.getPrerenderMarginRatio();
  };

  this.getPrerenderMarginRatio = function () {
    // See the readme for the description of `prerenderMarginRatio` option.
    return 1; // in scrollable container heights.
  };
  /**
   * Calls `onItemFirstRender()` for items that haven't been
   * "seen" previously.
   * @param  {any[]} items
   * @param  {number[]} itemHeights
   * @param  {number} firstShownItemIndex
   * @param  {number} lastShownItemIndex
   */


  this.onBeforeShowItems = function (items, itemHeights, firstShownItemIndex, lastShownItemIndex) {
    if (_this.onItemInitialRender) {
      var i = firstShownItemIndex;

      while (i <= lastShownItemIndex) {
        if (itemHeights[i] === undefined) {
          _this.onItemInitialRender(items[i]);
        }

        i++;
      }
    }
  };

  this.measureItemHeightsAndSpacing = function () {
    // Measure "newly shown" item heights.
    // Also re-validate already measured items' heights.
    _this.itemHeights.measureItemHeights(_this.getState().firstShownItemIndex, _this.getState().lastShownItemIndex); // Measure item vertical spacing, if required.


    var verticalSpacing = _this.measureVerticalSpacingIfNotMeasured(); // Return a state update if vertical spacing has been measured.
    // Doesn't set `verticalSpacing: 0` in `state` because it is effectively
    // same as `verticalSpacing: undefined` in terms code behavior and calculations.
    // Not having `verticalSpacing: 0` in `state` just makes the `state` object
    // a bit more cleaner and a bit less cluttered (easier for inspection).


    if (verticalSpacing && verticalSpacing !== 0) {
      // Return a state update.
      // Sets `verticalSpacing` property in `state`.
      return {
        verticalSpacing: verticalSpacing
      };
    }
  };

  this.cancelLayoutTimer = function (_ref3) {
    var stateUpdate = _ref3.stateUpdate;

    if (_this.layoutTimer) {
      unscheduleOnNextTick(_this.layoutTimer);
      _this.layoutTimer = undefined; // Merge state updates.

      if (stateUpdate || _this.layoutTimerStateUpdate) {
        stateUpdate = _objectSpread(_objectSpread({}, _this.layoutTimerStateUpdate), stateUpdate);
        _this.layoutTimerStateUpdate = undefined;
        return stateUpdate;
      }
    } else {
      return stateUpdate;
    }
  };

  this.scheduleLayoutTimer = function (_ref4) {
    var reason = _ref4.reason,
        stateUpdate = _ref4.stateUpdate;
    _this.layoutTimerStateUpdate = stateUpdate;
    _this.layoutTimer = scheduleOnNextTick(function () {
      _this.layoutTimerStateUpdate = undefined;
      _this.layoutTimer = undefined;

      _this.onUpdateShownItemIndexes({
        reason: reason,
        stateUpdate: stateUpdate
      });
    });
  };
}
var FPS = 60; // though 120 fps is common on smartphones these days.

var FRAME_DURATION = 1000 / FPS; // in milliseconds
// Layout is considered "too slow" when it "blocks" the "main thread"
// for so long that it starts causing "frame drops".

var SLOW_LAYOUT_DURATION = FRAME_DURATION;
//# sourceMappingURL=VirtualScroller.layout.js.map